import { useFieldState } from 'informed';
import { parsePhoneNumber } from 'libphonenumber-js';
import { type FC, useMemo } from 'react';
import styled from 'styled-components';
import { countriesWithPhoneNumber } from '@/helpers/countriesWithPhoneNumber';
import { ThemeMode } from '@/helpers/enums';
import { maskPhone } from '@/helpers/masks';
import type { WithTestId } from '@/helpers/testIds';
import { required, validatePhone } from '@/helpers/validators';
import { useRegModalTheme } from '@/modules/registration/hooks/useRegModalTheme';
import { device } from '@/styles/helpers';
import { Input } from './Input';
import { Select } from './Select';

interface InputPhoneNumberProps extends WithTestId {
  name: string;
  initialValue?: string;
  initialCodeValue?: string;
  validateOnBlur?: boolean;
  withValidate?: boolean;
  disabled?: boolean;
  error?: string;
  mode?: ThemeMode;
  onBlur?: () => void;
}

export const InputPhoneNumber: FC<InputPhoneNumberProps> = ({
  name,
  initialValue,
  initialCodeValue,
  disabled,
  testId,
  mode = ThemeMode.Light,
  ...props
}) => {
  const fieldCodeState = useFieldState<string | undefined>(`${name}.code`);
  const { styleScheme, input, select } = useRegModalTheme();

  const countriesOptions = useMemo(
    () =>
      countriesWithPhoneNumber.map(({ name, flagUrl, dialCode, iso2 }) => ({
        icon: flagUrl,
        name: `+${dialCode} | ${name}`,
        nameS: `+${dialCode} | ${name}`,
        nameV: `+${dialCode}`,
        value: iso2,
      })),
    []
  );

  const {
    country: initialValueCountry,
    nationalNumber: initialValueNationalNumber,
  } = useMemo(() => {
    const isFullNumber =
      typeof initialValue === 'string' && initialValue?.includes('+');

    if (!(initialValue && isFullNumber) && !!initialCodeValue) {
      return {
        country: initialCodeValue,
        nationalNumber: initialValue,
      };
    }

    if (!(initialValue && isFullNumber))
      return {
        country: undefined,
        nationalNumber: initialValue,
      };

    return parsePhoneNumber(initialValue);
  }, [initialValue, initialCodeValue]);

  const currentCountry = useMemo(() => {
    if (!(fieldCodeState && fieldCodeState.value)) return initialValueCountry;

    return fieldCodeState.value;
  }, [fieldCodeState, initialValueCountry]);

  return (
    <ScInputPhoneNumber>
      <Select
        cols={1}
        disabled={disabled}
        initialValue={initialValueCountry}
        mode={mode}
        name={`${name}.code`}
        options={countriesOptions}
        placeholder="Prefix"
        scrollBlockTopMargin={0}
        search
        styleScheme={styleScheme}
        testId={testId ? `${testId}-code` : undefined}
        validate={required}
        withInsideLabel={select.withInsideLabel}
      />
      <Input
        {...props}
        disabled={disabled}
        initialValue={initialValueNationalNumber as string}
        internalLabel={input.withInsideLabel ? 'Number' : ''}
        key={currentCountry}
        mode={mode}
        name={`${name}.number`}
        parser={(v) => maskPhone(v?.toString(), currentCountry)}
        placeholder={input.withInsideLabel ? '' : 'Number'}
        styleScheme={styleScheme}
        testId={testId ? `${testId}-number` : undefined}
        validate={(v) => validatePhone(v, currentCountry)}
        withInsideLabel={input.withInsideLabel}
      />
    </ScInputPhoneNumber>
  );
};

const ScInputPhoneNumber = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 160px auto;
  grid-column-gap: 8px;
  grid-row-gap: 8px;

  @media (${device.mobile}) {
    grid-template-columns: 150px auto;
  }
`;
